import { useEffect, useState } from 'react';
import { Card } from '../components/ui/card';
import { Target, Eye, Lightbulb, Shield, Heart, Award, TrendingUp } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const About = () => {
  const [companyInfo, setCompanyInfo] = useState(null);

  useEffect(() => {
    const fetchCompanyInfo = async () => {
      try {
        const response = await axios.get(`${API}/company/info`);
        setCompanyInfo(response.data);
      } catch (error) {
        console.error('Error fetching company info:', error);
      }
    };
    fetchCompanyInfo();
  }, []);

  const values = [
    { title: 'Innovation', description: 'We constantly innovate to bring unique experiences to our customers.', icon: <Lightbulb className="w-6 h-6" /> },
    { title: 'Trust', description: 'Building trust with customers, restaurants, and delivery partners.', icon: <Shield className="w-6 h-6" /> },
    { title: 'Quality', description: 'Ensuring the highest quality in every order delivered.', icon: <Award className="w-6 h-6" /> },
    { title: 'Accessibility', description: 'Making great food accessible to everyone across India.', icon: <Heart className="w-6 h-6" /> },
  ];

  return (
    <div className="min-h-screen pt-20" data-testid="about-page">
      {/* Hero Section */}
      <section className="fudco-hero-gradient fudco-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fade-in-up">
              <div className="inline-flex items-center gap-2 bg-green-100 text-[#2D8B2E] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <TrendingUp className="w-4 h-4" />
                About Fudco
              </div>
              <h1 className="fudco-heading-1 text-gray-900 mb-6">
                India's First <span className="text-[#2D8B2E]">Food Stock Market</span>
              </h1>
              <p className="fudco-body-lg mb-6">
                {companyInfo?.tagline || "We're revolutionizing food delivery by introducing a stock market-inspired ecosystem where food prices fluctuate based on demand."}
              </p>
              <div className="flex gap-6">
                <div>
                  <p className="text-3xl font-bold text-[#2D8B2E]" style={{ fontFamily: 'Outfit, sans-serif' }}>2025</p>
                  <p className="text-sm text-gray-600">Founded</p>
                </div>
                <div>
                  <p className="text-3xl font-bold text-[#2D8B2E]" style={{ fontFamily: 'Outfit, sans-serif' }}>Pan India</p>
                  <p className="text-sm text-gray-600">Operations</p>
                </div>
              </div>
            </div>
            <div className="animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              <img
                src="https://images.unsplash.com/photo-1643962579745-bcaa05ffc573?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzB8MHwxfHNlYXJjaHwzfHxzdG9jayUyMG1hcmtldCUyMHRyYWRpbmclMjBncmFwaCUyMGdyZWVuJTIwYnVsbGlzaHxlbnwwfHx8fDE3NzAwMzc5Njh8MA&ixlib=rb-4.1.0&q=85"
                alt="Food Stock Market Concept"
                className="rounded-3xl shadow-2xl w-full h-[400px] object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="fudco-section bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-8">
            {/* Mission */}
            <Card className="p-8 md:p-12 bg-gradient-to-br from-green-50 to-white border-green-100 hover-lift" data-testid="mission-card">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-[#2D8B2E] text-white p-4 rounded-2xl">
                  <Target className="w-8 h-8" />
                </div>
                <h2 className="fudco-heading-2 text-gray-900">Our Mission</h2>
              </div>
              <p className="fudco-body-lg">
                {companyInfo?.mission || "To revolutionize food delivery by creating India's first Food Stock Market, making quality food accessible and affordable for everyone while empowering restaurants and delivery partners."}
              </p>
            </Card>

            {/* Vision */}
            <Card className="p-8 md:p-12 bg-gradient-to-br from-yellow-50 to-white border-yellow-100 hover-lift" data-testid="vision-card">
              <div className="flex items-center gap-4 mb-6">
                <div className="bg-[#FFD700] text-black p-4 rounded-2xl">
                  <Eye className="w-8 h-8" />
                </div>
                <h2 className="fudco-heading-2 text-gray-900">Our Vision</h2>
              </div>
              <p className="fudco-body-lg">
                {companyInfo?.vision || "To become India's most trusted and innovative food technology platform, connecting millions of customers with their favorite restaurants through a unique stock market-inspired ecosystem."}
              </p>
            </Card>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="fudco-section bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="fudco-heading-2 text-gray-900 mb-4">Our Core Values</h2>
            <p className="fudco-body-lg max-w-2xl mx-auto">
              The principles that guide everything we do at Fudco.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {values.map((value, index) => (
              <Card key={index} className="fudco-card text-center hover-lift" data-testid={`value-card-${index}`}>
                <div className="bg-green-100 text-[#2D8B2E] p-4 rounded-2xl inline-block mb-4">
                  {value.icon}
                </div>
                <h3 className="fudco-heading-3 text-gray-900 mb-3">{value.title}</h3>
                <p className="fudco-body">{value.description}</p>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-[#2D8B2E] fudco-section">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="fudco-heading-2 text-white mb-4">
            Join the Food <span className="text-[#FFD700]">Revolution</span>
          </h2>
          <p className="text-green-100 text-lg mb-8 max-w-2xl mx-auto">
            Be a part of India's first food stock market. Whether you're a customer, restaurant, or delivery partner, there's a place for you at Fudco.
          </p>
        </div>
      </section>
    </div>
  );
};
